import { useEffect, useMemo, useRef, useState } from "react";

// Country > State > City picker. Click the box, choose a country, then a state, then a city.
// The saved value is one text string: "City, State, Country" (or "Remote", or a shorter form when a
// country has no states / a state has no cities). Old values such as "Pune" are shown as they are.
// Data comes from the "country-state-city" package, loaded only the first time the box is opened.

const byName = (a, b) => a.name.localeCompare(b.name);

export default function LocationPicker({ value = "", onChange, placeholder = "Select location" }) {
  const [open, setOpen] = useState(false);
  const [geo, setGeo] = useState(null); // the loaded package
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState("country"); // country | state | city
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  const searchRef = useRef(null);

  // Load the data once, the first time the panel opens.
  useEffect(() => {
    if (!open || geo || loadError) return;
    import("country-state-city").then(setGeo).catch(() => setLoadError(true));
  }, [open, geo, loadError]);

  // Close when the user clicks outside the box.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => { if (open) searchRef.current?.focus(); }, [open, step]);

  const items = useMemo(() => {
    if (!geo) return [];
    const { Country, State, City } = geo;
    if (step === "country") return [...Country.getAllCountries()].sort(byName);
    if (step === "state") return [...State.getStatesOfCountry(country.isoCode)].sort(byName);
    const list = state
      ? City.getCitiesOfState(country.isoCode, state.isoCode)
      : City.getCitiesOfCountry(country.isoCode) || [];
    // Some data sets repeat a city name, so keep one of each.
    const seen = new Set();
    return list.filter((c) => (seen.has(c.name) ? false : seen.add(c.name))).sort(byName);
  }, [geo, step, country, state]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    const starts = items.filter((i) => i.name.toLowerCase().startsWith(q));
    const contains = items.filter((i) => !i.name.toLowerCase().startsWith(q) && i.name.toLowerCase().includes(q));
    return [...starts, ...contains];
  }, [items, query]);

  const reset = () => { setStep("country"); setCountry(null); setState(null); setQuery(""); };
  const finish = (text) => { onChange(text); setOpen(false); reset(); };

  const openPanel = () => { reset(); setOpen(true); };

  const chooseCountry = (c) => {
    const { State, City } = geo;
    setCountry(c);
    setQuery("");
    if (State.getStatesOfCountry(c.isoCode).length) { setState(null); setStep("state"); return; }
    if ((City.getCitiesOfCountry(c.isoCode) || []).length) { setState(null); setStep("city"); return; }
    finish(c.name); // nothing below the country in the data
  };

  const chooseState = (s) => {
    setState(s);
    setQuery("");
    if (geo.City.getCitiesOfState(country.isoCode, s.isoCode).length) { setStep("city"); return; }
    finish(`${s.name}, ${country.name}`);
  };

  const chooseCity = (c) => {
    finish([c.name, state?.name, country.name].filter(Boolean).join(", "));
  };

  const back = () => {
    setQuery("");
    if (step === "city" && state) { setStep("state"); setState(null); return; }
    setStep("country");
    setCountry(null);
    setState(null);
  };

  const onPick = (item) => {
    if (step === "country") chooseCountry(item);
    else if (step === "state") chooseState(item);
    else chooseCity(item);
  };

  const title = { country: "Select country", state: `Select state in ${country?.name}`, city: `Select city${state ? ` in ${state.name}` : ""}` }[step];

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="form-control"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ width: "100%", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
      >
        <span style={{ opacity: value ? 1 : 0.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", marginTop: 4,
            background: "var(--surface, #fff)", color: "inherit", border: "1px solid var(--border, #ccc)",
            borderRadius: 8, boxShadow: "0 6px 18px rgba(0,0,0,.15)", overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--border, #ddd)" }}>
            {step !== "country" && (
              <button type="button" onClick={back} aria-label="Go back" style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit", fontSize: 16 }}>
                ←
              </button>
            )}
            <strong style={{ fontSize: 13 }}>{title}</strong>
          </div>

          {/* Steps shown as a trail so the user sees what is already chosen */}
          {(country || state) && (
            <div style={{ padding: "4px 10px", fontSize: 12, opacity: 0.75 }}>
              {[country?.name, state?.name].filter(Boolean).join(" › ")}
            </div>
          )}

          <div style={{ padding: "8px 10px" }}>
            <input
              ref={searchRef}
              className="form-control"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${step}`}
              autoComplete="off"
            />
          </div>

          <ul role="listbox" style={{ listStyle: "none", margin: 0, padding: "0 4px 4px", maxHeight: 260, overflowY: "auto" }}>
            {step === "country" && !query.trim() && (
              <li
                role="option"
                aria-selected={value === "Remote"}
                onClick={() => finish("Remote")}
                style={{ padding: "6px 10px", cursor: "pointer", fontStyle: "italic" }}
              >
                Remote
              </li>
            )}
            {loadError && <li style={{ padding: "8px 10px" }}>Could not load the location list. Check that country-state-city is installed.</li>}
            {!geo && !loadError && <li style={{ padding: "8px 10px", opacity: 0.7 }}>Loading...</li>}
            {geo && shown.length === 0 && <li style={{ padding: "8px 10px", opacity: 0.7 }}>No match.</li>}
            {shown.map((item) => (
              <li
                key={`${item.isoCode || ""}${item.name}`}
                role="option"
                aria-selected={value === item.name || value.startsWith(`${item.name},`)}
                onClick={() => onPick(item)}
                style={{ padding: "6px 10px", cursor: "pointer" }}
              >
                {item.name}
              </li>
            ))}
          </ul>

          {step !== "country" && country && (
            <div style={{ padding: "6px 10px", borderTop: "1px solid var(--border, #ddd)", fontSize: 12 }}>
              <button
                type="button"
                onClick={() => finish(step === "city" && state ? `${state.name}, ${country.name}` : country.name)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit", textDecoration: "underline", padding: 0 }}
              >
                Use {step === "city" && state ? state.name : country.name} only
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}